import { useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';

export interface RewardNotification {
  type: 'achievement';
  id: string;
  name: string;
  description: string;
  icon: string | null;
  points: number;
  unlockedAt: string;
}

const DEDUP_WINDOW_MS = 60_000;

export function useRewardNotifications() {
  const { customer, token, isAuthenticated } = useCustomerAuth();
  const [queue, setQueue] = useState<RewardNotification[]>([]);
  const shownRecently = useRef<Map<string, number>>(new Map());
  const socketRef = useRef<Socket | null>(null);

  const dismiss = useCallback(() => {
    setQueue((q) => q.slice(1));
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !customer?.id || !token) return;

    const socketUrl =
      import.meta.env.VITE_API_URL ||
      `${window.location.protocol}//${window.location.hostname}:3001`;

    const socket = io(socketUrl, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join-customer-room', { customerId: customer.id, token });
    });

    socket.on(
      'achievement-unlocked',
      (achievements: Array<{ id: string; name: string; description: string; icon: string | null; points: number; unlockedAt: string }>) => {
        const now = Date.now();
        for (const [id, ts] of shownRecently.current) {
          if (now - ts > DEDUP_WINDOW_MS) shownRecently.current.delete(id);
        }
        const fresh = achievements.filter((a) => {
          if (shownRecently.current.has(a.id)) return false;
          shownRecently.current.set(a.id, now);
          return true;
        });
        if (fresh.length > 0) {
          setQueue((q) => [
            ...q,
            ...fresh.map((a) => ({ type: 'achievement' as const, ...a })),
          ]);
        }
      }
    );

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [isAuthenticated, customer?.id, token]);

  return { current: queue[0] ?? null, dismiss, queueLength: queue.length };
}
